import {
	CONTEST_SCOPABLE_REASONS,
	type ContestScopableReason,
	ContestSpecialEventKind,
	type ContestSpecialEventParams,
} from "@repo/api-dtos"
import type { AdminSpecialEvent } from "@repo/marketplace-admin"

export type { AdminSpecialEvent }

export const SPECIAL_EVENT_KIND_LABELS: Record<
	ContestSpecialEventKind,
	string
> = {
	[ContestSpecialEventKind.Boost]: "Boost",
}

export const SCOPABLE_REASON_LABELS: Record<ContestScopableReason, string> = {
	referral_signup: "Signups",
	referral_signup_upline: "Upline signups",
	referee_app_install: "App installs",
	referee_first_purchase: "First purchases",
	referee_first_sale: "First sales",
	referee_both: "Bought and sold",
}

/** "×2 on Signups, App installs", or "×2 on every action". */
export function describeSpecialEvent(
	params: ContestSpecialEventParams,
): string {
	switch (params.kind) {
		case ContestSpecialEventKind.Boost: {
			const actions =
				params.reasons.length === CONTEST_SCOPABLE_REASONS.length
					? "every action"
					: params.reasons
							.map((reason) => SCOPABLE_REASON_LABELS[reason])
							.join(", ")
			return `×${params.multiplier} on ${actions}`
		}
		default: {
			const unknownKind: never = params.kind
			return unknownKind
		}
	}
}

/** Special events applied to one ledger row, read from its meta. */
export function appliedSpecialEvents(
	meta: Record<string, unknown> | null,
): string {
	const applied = meta?.specialEvents
	if (!Array.isArray(applied)) return ""
	return applied
		.map((entry: unknown) => {
			if (typeof entry !== "object" || entry === null) return null
			const multiplier = "multiplier" in entry ? entry.multiplier : undefined
			return typeof multiplier === "number" ? `×${multiplier}` : null
		})
		.filter((label): label is string => label !== null)
		.join(" ")
}

export const SPECIAL_EVENT_INVALID_MESSAGES = {
	dates: "The end must come after the start.",
	in_past: "A special event cannot start in the past.",
	too_soon: "A special event must start at least a minute from now.",
	outside_window: "A special event must sit inside the contest dates.",
	kind_changed: "The kind of a special event cannot change.",
} as const

/** Operator-facing message for a CONTEST_SPECIAL_EVENT_OVERLAP refusal. */
export function specialEventOverlapMessage(
	reasons: ContestScopableReason[],
): string {
	const actions = reasons.map((reason) => SCOPABLE_REASON_LABELS[reason])
	return `Another boost already runs at that time on: ${actions.join(", ")}.`
}
